import React, { createContext, useState, useEffect, useContext } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { saveToCache, getFromCache, CACHE_KEYS } from '../services/domain/cacheService';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/routes';

// Define user type
export interface AuthUser {
  id: string;
  email: string;
  name?: string;
  avatar_url?: string;
  isPremium?: boolean;
  createdAt?: string;
}

export type AuthState = {
  user: AuthUser | null;
  session: Session | null;
  isLoading: boolean;
};

// Define the context type
interface AuthContextType {
  user: AuthUser | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string, returnTo?: string) => Promise<void>;
  signUp: (email: string, password: string, metadata?: any, returnTo?: string) => Promise<{needsEmailVerification: boolean, user: any}>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<AuthUser | null>;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isLoading: true,
  isAuthenticated: false,
  signIn: async () => {},
  signUp: async () => ({ needsEmailVerification: false, user: null }),
  logout: async () => {},
  refreshUser: async () => null
});

// Export the hook directly without wrapping in a function declaration
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * AuthProvider component
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    session: null,
    isLoading: true
  });
  const navigate = useNavigate();
  
  // Get the current user session
  const getCurrentUser = async (): Promise<AuthState> => {
    try {
      // Check for existing session
      const { data } = await supabase.auth.getSession();
      console.log("GET SESSION RESULT:", data);
      
      if (data?.session) {
        const { data: userData } = await supabase.auth.getUser();
        console.log("GET USER RESULT:", userData);
        
        if (userData?.user) {
          // Get profile data if available
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userData.user.id)
            .single();
          
          console.log("PROFILE DATA:", profileData, "ERROR:", profileError);
          
          // Enhanced user with profile data - handle case where profile might not exist yet
          const enhancedUser = {
            id: userData.user.id,
            email: userData.user.email || '',
            name: profileData?.full_name || userData.user.user_metadata?.full_name || '',
            isPremium: profileData?.is_premium || false,
            avatar_url: profileData?.avatar_url || userData.user.user_metadata?.avatar_url,
            createdAt: userData.user.created_at
          };
          
          // If profile doesn't exist but we have a user, create the profile
          if (profileError && profileError.code === 'PGRST116') {
            console.log("Profile doesn't exist yet, creating one...");
            try {
              const { error: insertError } = await supabase
                .from('profiles')
                .insert([
                  { 
                    id: userData.user.id, 
                    email: userData.user.email || '',
                    full_name: userData.user.user_metadata?.full_name || '',
                    is_premium: false
                  }
                ]);
                
              if (insertError) {
                console.error("Failed to create profile:", insertError);
              }
            } catch (err) {
              console.error("Error creating profile:", err);
            }
          }
          
          // Cache user for offline access
          saveToCache(CACHE_KEYS.USER, enhancedUser);
          
          console.log("ENHANCED USER:", enhancedUser);
          
          return { 
            user: enhancedUser, 
            session: data.session,
            isLoading: false
          };
        }
      }
      
      // If no online session, try to get from cache for offline functionality
      const cachedUser = getFromCache<AuthUser>(CACHE_KEYS.USER);
      return {
        user: cachedUser,
        session: null,
        isLoading: false
      };
    } catch (error) {
      console.error('Error getting current user:', error);
      
      // Fallback to cache if online fetch fails
      const cachedUser = getFromCache<AuthUser>(CACHE_KEYS.USER);
      return {
        user: cachedUser,
        session: null,
        isLoading: false
      };
    }
  };
  
  useEffect(() => {
    // Initial user load
    const loadUser = async () => {
      try {
        console.log("Initial auth state check starting...");
        // First check for current session
        const state = await getCurrentUser();
        console.log("Initial auth state:", state);
        setAuthState(state);
      } catch (error) {
        console.error("Error during initial auth check:", error);
        setAuthState(prev => ({...prev, isLoading: false}));
      }
    };
    
    loadUser();
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session);
        
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
          console.log("Updating auth state after", event);
          const state = await getCurrentUser();
          console.log("New auth state:", state);
          setAuthState(state);
        } else if (event === 'SIGNED_OUT') {
          console.log("User signed out, clearing auth state");
          setAuthState({
            user: null,
            session: null,
            isLoading: false
          });
        }
      }
    );
    
    // Cleanup
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleSuccessfulAuth = (user: any, session: any, returnTo?: string) => {
    const enhancedUser = {
      id: user.id,
      email: user.email || '',
      name: user.user_metadata?.full_name || '',
      isPremium: false,
      createdAt: user.created_at
    };

    setAuthState({
      user: enhancedUser,
      session: session,
      isLoading: false
    });
    
    // Navigate to return path if provided
    if (returnTo && typeof returnTo === 'string') {
      navigate(returnTo);
    }
  };
  
  // Sign in with email and password
  const signIn = async (email: string, password: string, returnTo?: string) => {
    setAuthState(state => ({ ...state, isLoading: true }));
    try {
      console.log("Starting sign-in process...");
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        console.error("Sign-in error:", error);
        throw error;
      }
      
      console.log("Sign-in successful:", data);
      
      if (data.user && data.session) {
        // Create enhanced user object
        const enhancedUser = {
          id: data.user.id,
          email: data.user.email || '',
          name: data.user.user_metadata?.full_name || '',
          isPremium: false,
          createdAt: data.user.created_at
        };

        // Update auth state
        setAuthState({
          user: enhancedUser,
          session: data.session,
          isLoading: false
        });
        
        // Handle proper navigation based on environment
        const baseUrl = window.location.origin;
        const isLocalDevelopment = baseUrl.includes('localhost') || 
          baseUrl.includes('127.0.0.1') || 
          baseUrl.match(/\d+\.\d+\.\d+\.\d+/);
        
        if (isLocalDevelopment) {
          console.log("Development environment detected, navigating to:", ROUTES.HOME);
          navigate(ROUTES.HOME);
        } else {
          // For production, use full URL without hash
          const destinationUrl = new URL(ROUTES.HOME, window.location.origin).toString();
          console.log("Production environment detected, navigating to:", destinationUrl);
          window.location.href = destinationUrl;
        }
        
        return;
      }
      
      const state = await getCurrentUser();
      setAuthState(state);
      navigate(ROUTES.HOME);
    } catch (error: any) {
      console.error("Sign-in process failed:", error);
      setAuthState(state => ({ ...state, isLoading: false }));
      throw error;
    }
  };
  
  // Sign up with email and password
  const signUp = async (email: string, password: string, metadata?: any, returnTo?: string) => {
    setAuthState(state => ({ ...state, isLoading: true }));
    try {
      console.log("Starting signup process...");
      
      // Proceed with signup
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { 
          data: {
            full_name: metadata?.name || '',
            is_premium: false
          },
          emailRedirectTo: `${window.location.origin}/login?verified=true`
        }
      });
      
      console.log("Signup response:", data, error);
      
      if (error) throw error;
      
      if (data.user) {
        console.log("Creating profile record for new user");
        
        const { error: profileError } = await supabase
          .from('profiles')
          .insert([
            { 
              id: data.user.id, 
              email: email,
              full_name: metadata?.name || '',
              is_premium: false
            }
          ]);
        
        if (profileError) {
          console.error("Error creating profile:", profileError);
        }
        
        // If we have session, user doesn't need email verification
        if (data.session) {
          handleSuccessfulAuth(data.user, data.session, returnTo);
        }
      }
      
      // At this point, user needs email verification
      const state = {
        user: data.user ? {
          id: data.user.id,
          email: data.user.email || '',
          name: metadata?.name || '',
          isPremium: false,
          createdAt: data.user.created_at
        } : null,
        session: data.session,
        isLoading: false
      };
      
      setAuthState(state);
      
      return {
        needsEmailVerification: !data.session,
        user: data.user
      };
    } catch (error: any) {
      console.error("Signup error:", error);
      setAuthState(state => ({ ...state, isLoading: false }));
      throw error;
    }
  };
  
  // Update the logout function
  const logout = async () => {
    try {
      console.log("Starting logout process...");
      
      // 1. Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Supabase signout error:', error);
      }
      
      // 2. Clear cached user data
      localStorage.removeItem(CACHE_KEYS.USER);
      sessionStorage.clear();
      
      // 3. Dispatch event for other components to react to logout
      window.dispatchEvent(new CustomEvent('auth-logout'));
      
      // 4. Force state update
      setAuthState({
        user: null,
        session: null,
        isLoading: false
      });
      
      // 5. Handle proper navigation based on environment
      const baseUrl = window.location.origin;
      const isLocalDevelopment = baseUrl.includes('localhost') || 
        baseUrl.includes('127.0.0.1') || 
        baseUrl.match(/\d+\.\d+\.\d+\.\d+/);
      
      if (isLocalDevelopment) {
        console.log("Development environment detected, navigating to:", ROUTES.HOME);
        navigate(ROUTES.HOME, { replace: true });
      } else {
        // For production, use full URL without hash
        const destinationUrl = new URL(ROUTES.HOME, window.location.origin).toString();
        console.log("Production environment detected, navigating to:", destinationUrl);
        window.location.href = destinationUrl;
      }
    } catch (error) {
      console.error('Logout error:', error);
      
      // Even if there's an error, still clear the state
      setAuthState({
        user: null,
        session: null,
        isLoading: false
      });
      
      navigate(ROUTES.HOME, { replace: true });
    }
  };
  
  // Refresh user data
  const refreshUser = async () => {
    try {
      console.log("Refreshing user data...");
      const state = await getCurrentUser();
      console.log("Refreshed auth state:", state);
      setAuthState(state);
      return state.user;
    } catch (error) {
      console.error('Error refreshing user:', error);
      return null;
    }
  };
  
  // Context value
  const contextValue: AuthContextType = {
    user: authState.user,
    session: authState.session,
    isLoading: authState.isLoading,
    isAuthenticated: !!authState.user,
    signIn,
    signUp,
    logout,
    refreshUser
  };
  
  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}